import React, { useEffect, useState } from 'react';
import {
  InstallmentCosts,
  InstallmentDetails,
  InstallmentRepaymentDetails,
} from '@/features/application/components';
import styles from './InstallmentConfirmationPage.module.scss';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { useBankCard } from '@/features/bankCard/hooks/useBankCard';
import { useAddBankCardRedirect } from '@/features/bankCard/hooks/useAddBankCardRedirect';
import { BankCardResultSheet } from '@/features/bankCard/components/BankCardResultSheet';
import { BankCardSelectSheet } from '@/features/bankCard/components/BankCardSelectSheet';
import {
  AddBankCardResultTypes,
  BankCardState,
  type BankCardResultState,
} from '@/features/bankCard/types/bankCard';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { useLoanOffer } from '@/features/offer';
import { useApplication } from '../../hooks/useApplication';
import { calculateInstallmentLoanDetails } from '@/features/offer/hooks/loanOfferUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { OceanOfferHero } from '@/features/offer/components/OceanOfferHero';
import {
  FinalOfferChangesBanner,
  type OfferChangesState,
} from '../../components/FinalOfferChangesBanner';
import { routes } from '@/routes';
import { useError } from '@/providers/Error';
import { genericMessages } from '@/constants/errorMessages';
import { useLoan } from '@/features/loan/hooks/useLoan';
import { useKyc } from '@/features/kyc/hooks/useKyc';
import { trackingService } from '@/lib/tracking';

export interface InstallmentConfirmationPageProps {
  className?: string;
}

export const InstallmentConfirmationPage: React.FC<
  InstallmentConfirmationPageProps
> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Opened from the chosen preliminary credit, which ANAF changed.
  const [offerChanges, setOfferChanges] = useState(
    () => (location.state as OfferChangesState | null)?.offerChanges
  );
  // Back from the card partner: its result shows once, so a reload or Back
  // doesn't repeat it.
  const [bankCardResult, setBankCardResult] = useState(
    () =>
      (location.state as BankCardResultState | null)?.bankCardResult as
        | AddBankCardResultTypes
        | undefined
  );
  useEffect(() => {
    if (bankCardResult) {
      navigate(location.pathname, {
        replace: true,
        state: { ...(location.state as object), bankCardResult: undefined },
      });
    }
  }, []);
  const { setError } = useError();
  const {
    bankCards,
    defaultBankCard,
    isLoading: isBankCardsLoading,
  } = useBankCard(true);
  const [isCardSelectOpen, setIsCardSelectOpen] = useState(false);
  const {
    startAddBankCard,
    isRedirecting: isAddingBankCard,
    error: addBankCardError,
  } = useAddBankCardRedirect();
  const {
    installmentDraft,
    setInstallmentBankCardId,
    applyOffer,
    isApplyingOffer,
    applyToOfferError,
    getApplicationState,
  } = useApplication();
  const {
    installmentOffer,
    loanOfferState,
    isLoading: isLoanOfferLoading,
  } = useLoanOffer(true);
  const { requestContractSignCode } = useLoan();
  const { requiresProofOfAddressVerification } = useKyc(true);

  // event_id is the scoring id, so repeat visits on the same offers dedupe.
  useEffect(() => {
    if (isLoanOfferLoading) {
      return;
    }
    void trackingService.trackEvent('final_offer_summary_shown', {
      type: 'installment',
      event_id: loanOfferState?.id ?? undefined,
    });
  }, [isLoanOfferLoading]);

  const details = calculateInstallmentLoanDetails(
    installmentOffer,
    installmentDraft?.amount,
    installmentDraft?.numberOfRates
  );

  // Cards the money can be paid to; the main one unless the user chose another.
  const usableBankCards = bankCards.filter(
    (card) => card.isActive || card.cardState === BankCardState.Active
  );
  const bankCard =
    usableBankCards.find((card) => card.id === installmentDraft?.bankCardId) ??
    defaultBankCard;

  // A chosen card no longer usable: the main one is shown, and sent.
  const chosenCardId = installmentDraft?.bankCardId;
  useEffect(() => {
    if (
      !isBankCardsLoading &&
      chosenCardId &&
      !usableBankCards.some((card) => card.id === chosenCardId)
    ) {
      setInstallmentBankCardId(undefined);
    }
  }, [isBankCardsLoading, chosenCardId, bankCards]);

  // A card just added at the partner is the one the money goes to.
  useEffect(() => {
    if (bankCardResult !== AddBankCardResultTypes.Success || isBankCardsLoading) {
      return;
    }
    setInstallmentBankCardId(bankCards.find((card) => card.isNew)?.id);
  }, [bankCardResult, isBankCardsLoading]);

  const isDisabled = !details || isApplyingOffer;

  useEffect(() => {
    if (applyToOfferError) {
      setError(genericMessages.unexpected);
      return;
    }
  }, [applyToOfferError]);

  useEffect(() => {
    if (addBankCardError) {
      setError(genericMessages.unexpected);
    }
  }, [addBankCardError]);

  const handleConfirm = async () => {
    if (!installmentOffer || !details) {
      throw new Error('Installment offer not found');
    }

    if (!bankCard) {
      await startAddBankCard();
      return;
    }

    if (requiresProofOfAddressVerification) {
      navigate(routes.kyc.proofOfAddress);
      return;
    }

    const contract = await applyOffer(
      installmentOffer.id,
      installmentOffer.type,
      installmentOffer.creditProductName,
      details.selectedAmount,
      details.selectedNumberOfRates,
      undefined,
      details.isDerogation,
      // Declared on the signing screen, without which the contract is not
      // signed.
      true
    );

    if (!contract) {
      throw new Error('Failed to apply offer');
    }

    await getApplicationState();
    await requestContractSignCode(contract.id);
    navigate(routes.loan.sign);
  };

  const handleEditPeriod = () => {
    navigate(routes.application.calculatorTab('installment'));
  };

  const handleEditBankCard = () => {
    void startAddBankCard();
  };

  const handleEditCreditAmount = () => {
    navigate(routes.application.calculatorTab('installment'));
  };

  if (isBankCardsLoading) {
    return <PageLoader />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {/* Main Content */}
        <div className={styles.mainContent}>
          {installmentOffer && (
            <OceanOfferHero
              type="installment"
              amount={details?.selectedAmount ?? installmentOffer.creditLimit}
              numberOfRates={
                details?.selectedNumberOfRates ?? installmentOffer.numberOfRates
              }
            />
          )}
          {offerChanges && (
            <FinalOfferChangesBanner
              changes={offerChanges}
              onClose={() => setOfferChanges(undefined)}
            />
          )}
          {/* Repayment Details Section */}
          <InstallmentRepaymentDetails
            lastPaymentDate={details?.lastRateDate}
            bankCard={bankCard?.cardMask?.replace(/\*/g, '\u2022') ?? ''}
            showEditIcons={bankCard ? false : true}
            onEditBankCard={handleEditBankCard}
            onChangeBankCard={() => setIsCardSelectOpen(true)}
          />

          {/* Costs Section */}
          <InstallmentCosts
            creditAmount={details?.selectedAmount}
            analysisFee={details?.grantingCommission}
            totalToRepay={details?.finalPaymentAmount}
            showEditIcons={true}
            interestRate={details?.annualInterest}
            annualRate={details?.dae}
            onEditCreditAmount={handleEditCreditAmount}
          />

          {/* Details Section */}
          <InstallmentDetails
            creditAmount={details?.creditAmount}
            monthlyPayment={details?.monthlyPayment}
            period={details?.selectedNumberOfRates}
            showEditIcons={true}
            onEditPeriod={handleEditPeriod}
          />
        </div>
      </div>
      <div className={styles.footer}>
        {bankCard ? (
          <>
            <Button
              trackingId="confirm_installment"
              className={styles.button}
              text="Continuă spre contract"
              rightIcon={<Icon name="arrow-right" />}
              onClick={handleConfirm}
              loading={isApplyingOffer}
              disabled={isDisabled}
            />
          </>
        ) : (
          <>
            {/* The money is paid to a card: it comes before the contract. */}
            <Button
              trackingId="installment_add_bank_card"
              className={styles.button}
              text="Adaugă card"
              loading={isAddingBankCard}
              onClick={handleEditBankCard}
            />
            <p className={styles.cardCaption}>Pe care vrei să primești banii.</p>
          </>
        )}
      </div>
      <BankCardSelectSheet
        open={isCardSelectOpen}
        onClose={() => setIsCardSelectOpen(false)}
        bankCards={usableBankCards}
        selectedCardId={bankCard?.id}
        onSave={setInstallmentBankCardId}
        onAddCard={startAddBankCard}
        isAddingCard={isAddingBankCard}
      />
      <BankCardResultSheet
        result={bankCardResult}
        onClose={() => setBankCardResult(undefined)}
        onRetry={startAddBankCard}
        isRetrying={isAddingBankCard}
      />
    </div>
  );
};
